import { useEffect, useRef, useState } from 'react';
import { deepDiff } from '@forvalter/utils/deepDiff';

import { useCalculateKalkylePayload } from '../context/useCalculateSummaryPayload';
import { useKostnadsKalkyleContext } from '../context/KostnadsKalkyleProvider';
import { useNokkeltallSammendrag } from '../context/useNokkeltallSammendrag';

/**
 * Autosave component that saves form changes after calculations complete.
 * - Prevents saving on initial form load
 * - Waits for calculate endpoint to complete before saving
 * - Uses enriched payload with kostnad in delsummer
 */
export const KostnadsKalkyleFormAutosave = () => {
    const { createKostnadskjema, shouldInvalidateVurderingsskjema } =
        useKostnadsKalkyleContext();
    const { nokkeltallIsLoading } = useNokkeltallSammendrag();
    const payload = useCalculateKalkylePayload();

    const prevPayloadRef = useRef<typeof payload>();
    const [isInitialized, setIsInitialized] = useState(false);
    const [pendingSave, setPendingSave] = useState(false);

    // Trigger pending save when calculation completes
    useEffect(() => {
        if (!pendingSave || !isInitialized || nokkeltallIsLoading) {
            return;
        }

        createKostnadskjema({
            ...payload,
            shouldInvalidateVurderingsskjema,
        });
        setPendingSave(false);
        prevPayloadRef.current = payload; // Update ref to prevent duplicate save
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        nokkeltallIsLoading,
        pendingSave,
        isInitialized,
        payload,
        shouldInvalidateVurderingsskjema,
    ]);

    // Watch for user changes and trigger autosave
    useEffect(() => {
        const prevPayload = prevPayloadRef.current;
        const diff = deepDiff(prevPayload, payload);

        // Mark as initialized on first payload
        if (!isInitialized && payload) {
            setIsInitialized(true);
            prevPayloadRef.current = payload;
            return;
        }

        // Skip if no changes detected
        if (!diff) {
            return;
        }

        // Only save after form has been initialized
        if (!isInitialized || !prevPayload || !payload) {
            return;
        }

        if (nokkeltallIsLoading) {
            // Wait for calculation to complete
            setPendingSave(true);
            return;
        }

        // Save immediately if calculation is done
        createKostnadskjema({
            ...payload,
            shouldInvalidateVurderingsskjema,
        });

        prevPayloadRef.current = payload;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payload, shouldInvalidateVurderingsskjema]);

    return null;
};
